import React from "react";
import {AlertTooltip} from "./AlertTooltip";
import {ConversionsAccountsDetail, hasAccountDetail, hasDetailToShow} from "./ConversionsAccountsDetail";
import {PROPERTIES_FOR_METRIC} from "../../../metrics";
import {resolveMetricDisplayName, COLUMN_CELL_INVALID_CONTENT} from "../../../columns";
import { withTranslation } from 'react-i18next';

class GoalAlertTooltipClass extends AlertTooltip {
    render() {
        const { t } = this.props;
        let tooltipContent = this.tooltipContent();
        return (
            <div className="alert-tooltip">
                <div className="alert-tooltip-marker">
                    <i className="fa fa-circle" aria-hidden="true"/>
                </div>
                <div className="alert-tooltip-body">
                    <div className="alert-tooltip-header">
                        <div className="col-6 m-0 p-0"><p
                            className="alert-tooltip-title">{t('goal_alert_tooltip.goal')}
                            #{this.props.priority} {resolveMetricDisplayName(this.props.metricName)} = {this.props.formattedGoalValue}<span></span>
                        </p></div>
                        <div className="col-6 m-0 p-0 text-right"><span
                            className="font-weight-bold">{t('goal_alert_tooltip.day')}&nbsp;{this.props.dayNumber}{this.getTodayTitleText()}</span>
                        </div>

                    </div>
                    {tooltipContent}
                </div>
            </div>

        )
    }

    getFluctuationText() {
        const { t } = this.props;
        if (this.props.fluctuation === "up") {
            return t('goal_alert_tooltip.up');
        }

        if (this.props.fluctuation === "down") {
            return t('goal_alert_tooltip.down');
        }

        return "";
    }

    tooltipContent() {
        const { t } = this.props;
        return (
            <div className="alert-tooltip-content">
                <p className="alert-tooltip-description">
                    {this.getTextUpMetricAccumulate()} <span
                    className={"-" + this.props.alertLevel + " font-weight-bold"}><br/>{this.props.fluctuationPercentage} {this.getFluctuationText()}</span><br/><br/>
                    {/* `escapeValue: false` porque el nombre de una métrica personalizada puede
                        traer `&`, `'` o `/`, y el default de i18next los muestra como entidades. */}
                    <span style={{fontSize: "0.85em"}}>{t('goal_alert_tooltip.metric_of_the_day', {
                        metricName: resolveMetricDisplayName(this.props.metricName),
                        dayValue: this.props.dayValue,
                        interpolation: {escapeValue: false},
                    })}</span>
                </p>
                {/* Hermano del párrafo, no hijo: un <div> adentro de un <p> es anidamiento inválido
                    y React lo reporta en cada render. */}
                <div className={"conversions-by-action"}>{this.renderDayConversionActions()}</div>
                {this.renderAccumulatedDetail()}
            </div>
        );
    }

    /** El acumulado, con el MISMO render que la columna Conversiones (card jWnCzm8a): las dos
     *  columnas muestran el mismo desglose, así que tienen que contarlo igual. */
    renderAccumulatedDetail() {
        const clarifications = this.renderClarifications();
        if (!hasDetailToShow(this.props) && clarifications === null) {
            // Sin esto el separador de la sección aparece igual y se lee como "acá no hay datos".
            return null;
        }
        return (
            <div className="conv-tooltip-goal-detail">
                <ConversionsAccountsDetail conversionsByAccount={this.props.conversionsByAccount}
                                           valueByType={this.props.valueByType}
                                           notInTotalValueByType={this.props.notInTotalValueByType}
                                           conversionsSource={this.props.conversionsSource}/>
                {clarifications}
            </div>
        );
    }

    getTodayTitleText() {
        const { t } = this.props;
        if (this.props.isToday) {
            return "    [ " + t('goal_alert_tooltip.today') + " ]"
        }
    }

    getTextUpMetricAccumulate() {
        const { t } = this.props;
        // Custom metrics no están en PROPERTIES_FOR_METRIC → undefined.Accumulates crasheaba.
        // Sin valor medido, la frase de siempre queda como «…y el real obtenido es ---», que se lee
        // como un dato roto. Pasa cuando la plataforma no publica la métrica (ahí el ⓘ de la celda
        // explica el motivo) y en cualquier otro caso sin dato: en los dos, afirmar un número es
        // peor que decir que no lo hay.
        if (this.props.value === COLUMN_CELL_INVALID_CONTENT) {
            return <span>
                {t('goal_alert_tooltip.no_measured_value', {expectedValue: this.props.expectedValue})}
            </span>;
        }

        // Default false (no acumula) para CUSTOM:* y métricas desconocidas.
        const accumulates = (PROPERTIES_FOR_METRIC[this.props.metricName] || {})['Accumulates'] === true;
        if (accumulates) {
            return <span>
                {t('goal_alert_tooltip.expected_value_at_period', { expectedValue: this.props.expectedValue, actualValue: this.props.value })}
            </span>;
        } else {
            return <span>
                {t('goal_alert_tooltip.goal_vs_actual', { goal: this.props.expectedValue, actual: this.props.value })}
            </span>;
        }
    }

    /** El desglose del DÍA. No tiene equivalente agrupado por cuenta —el backend sólo guarda el
     *  acumulado así— y es información que sólo muestra esta columna: se deja como venía en vez de
     *  perderla. */
    renderDayConversionActions() {
        const { t } = this.props;
        if (!this.props.dayValueByType) {
            return [];
        }

        const rows = [];
        for (const [key, value] of Object.entries(this.props.dayValueByType)) {
            if (typeof value === "number" && value > 0) {
                rows.push(<span key={`day-${key}`}>- {key} : {value.toFixed(2)}<br/></span>);
            }
        }
        if (rows.length === 0) {
            return [];
        }

        return [<br key="day-br1"/>,
                <span key="day-title">
                    {t('goal_alert_tooltip.day_conversions_detail', { dayNumber: this.props.dayNumber })}
                </span>,
                <br key="day-br2"/>].concat(rows);
    }

    /** El estado de las cuentas cuando llega como texto mezclado entre los valores. Con el detalle
     *  agrupado cada cuenta trae su propio `status`, así que esto sólo aplica a las filas guardadas
     *  antes del cambio. */
    renderClarifications() {
        const { t } = this.props;
        if (hasAccountDetail(this.props.conversionsByAccount) || !this.props.valueByType) {
            return null;
        }

        const clarifications = Object.entries(this.props.valueByType)
            .filter(([, value]) => typeof value === 'string');
        if (clarifications.length === 0) {
            return null;
        }

        return (
            <div className="conv-tooltip-block">
                <p className="conv-tooltip-block-title">{t('goal_alert_tooltip.clarifications')}</p>
                {clarifications.map(([name, value]) => (
                    <p key={name} className="conv-tooltip-account-status">
                        <span className="name">{name}</span>: {value}
                    </p>
                ))}
            </div>
        );
    }
}

export const GoalAlertTooltip = withTranslation()(GoalAlertTooltipClass);
export default GoalAlertTooltip;
